import { useState } from 'react'
import type { ComponentProps } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FolderOpen, HelpCircle, MoreHorizontal, Plus } from 'lucide-react'
import { usePostHog } from '@posthog/react'
import {
  createWorkspace,
  deleteWorkspace,
  updateWorkspace,
  workspaceKeys,
  workspacesQueryOptions,
} from '#/api/workspaces'
import type { Workspace, WorkspaceStatus } from '#/api/workspaces'
import { workspaceTemplatesQueryOptions } from '#/api/workspace-templates'
import { Button } from '#/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog'
import { Input } from '#/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select'
import { DeleteConfirm } from '#/components/delete-confirm'
import { PageHeader } from '#/components/page-header'
import { PageContainer } from '#/components/page-container'
import { EmptyState } from '#/components/empty-state'
import {
  NewWorkspaceCard,
  WorkspaceCard,
} from '#/components/workspaces/workspace-card'
import { WorkspaceCreateDialog } from '#/components/workspaces/workspace-create-dialog'
import { WorkspaceExplainerDialog } from '#/components/workspaces/workspace-explainer-dialog'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'
import { hasPermission, useCan } from '#/user/me'
import { workspaceName } from '#/lib/display-name'

export const Route = createFileRoute(
  '/_authed/accounts/$accountId/workspaces/',
)({
  loader: ({ context }) =>
    // Workspace templates power the (manager-only) create flow; /workspace-templates
    // 403s without workspaces:manage, so only prefetch it for managers. Plain
    // members still get the workspace list from the $accountId layout's prefetch.
    hasPermission(context.me, 'annwn:workspaces:manage')
      ? context.queryClient.ensureQueryData(workspaceTemplatesQueryOptions)
      : undefined,
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: WorkspacesList,
})

type DialogState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; workspace: Workspace }
  | { kind: 'delete'; workspace: Workspace }

// The BFF's workspace list leaves out archived workspaces, so the filter does too.
const WORKSPACE_STATUSES: Array<WorkspaceStatus> = [
  'created',
  'initialised',
  'active',
  'complete',
]

function WorkspacesList() {
  const { t } = useTranslation()
  const { accountId } = Route.useParams()
  const { data: workspaces } = useSuspenseQuery(
    workspacesQueryOptions(accountId),
  )
  const queryClient = useQueryClient()
  const posthog = usePostHog()
  const canManage = useCan('annwn:workspaces:manage')
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [showExplainer, setShowExplainer] = useState(false)
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatus | 'all'>(
    'all',
  )

  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const filteredWorkspaces = workspaces.filter(
    (w) =>
      (statusFilter === 'all' || w.status === statusFilter) &&
      (w.name ?? '').toLowerCase().includes(query),
  )
  const noMatchesKey =
    query && statusFilter !== 'all'
      ? 'workspaces.noFilteredMatches'
      : query
        ? 'workspaces.noMatches'
        : 'workspaces.noStatusMatches'

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: workspaceKeys.all(accountId),
    })

  const createMutation = useMutation({
    mutationFn: (input: {
      name: string
      description: string | null
      workspace_template_uuid: string | null
    }) => createWorkspace(accountId, input),
    onSuccess: async (res, input) => {
      await invalidate()
      setDialog({ kind: 'closed' })
      posthog.capture('workspace_created', {
        account_id: accountId,
        workspace_id: res.uuid,
        workspace_name: input.name,
        has_template: input.workspace_template_uuid != null,
      })
    },
  })

  const editMutation = useMutation({
    mutationFn: ({
      uuid,
      ...input
    }: {
      uuid: string
      name: string
      description: string | null
    }) => updateWorkspace(accountId, uuid, input),
    onSuccess: async () => {
      await invalidate()
      setDialog({ kind: 'closed' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => deleteWorkspace(accountId, uuid),
    onSuccess: async (_, uuid) => {
      await invalidate()
      setDialog({ kind: 'closed' })
      posthog.capture('workspace_deleted', {
        account_id: accountId,
        workspace_id: uuid,
      })
    },
  })

  return (
    <PageContainer>
      <PageHeader
        title={t('workspaces.title')}
        actions={
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => setShowExplainer(true)}
            >
              <HelpCircle aria-hidden="true" />
              {t('workspaces.whatsAWorkspace')}
            </Button>
            {canManage ? (
              <Button
                type="button"
                onClick={() => setDialog({ kind: 'create' })}
              >
                <Plus aria-hidden="true" />
                {t('workspaces.new')}
              </Button>
            ) : null}
          </>
        }
      />

      <section className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('workspaces.searchPlaceholder')}
            aria-label={t('workspaces.searchPlaceholder')}
            className="w-full bg-card sm:w-64"
          />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as WorkspaceStatus | 'all')}
          >
            <SelectTrigger
              className="w-44 bg-card"
              aria-label={t('workspaces.statusFilterLabel')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('workspaces.allStatuses')}</SelectItem>
              {WORKSPACE_STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`workspaces.status.${status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {workspaces.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={t('workspaces.empty')}
            body={t('workspaces.emptyBody')}
            action={
              canManage ? (
                <Button onClick={() => setDialog({ kind: 'create' })}>
                  <Plus aria-hidden />
                  {t('workspaces.new')}
                </Button>
              ) : null
            }
          />
        ) : filteredWorkspaces.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">
            {t(noMatchesKey)}
          </p>
        ) : null}

        {workspaces.length === 0 ? null : (
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredWorkspaces.map((workspace) => (
              <li key={workspace.uuid}>
                <WorkspaceCard
                  accountId={accountId}
                  workspace={workspace}
                  actions={
                    canManage ? (
                      <WorkspaceCardMenu
                        name={workspaceName(t, workspace)}
                        onEdit={() => setDialog({ kind: 'edit', workspace })}
                        onDelete={() =>
                          setDialog({ kind: 'delete', workspace })
                        }
                      />
                    ) : undefined
                  }
                />
              </li>
            ))}
            {canManage ? (
              <li>
                <NewWorkspaceCard
                  onCreate={() => setDialog({ kind: 'create' })}
                />
              </li>
            ) : null}
          </ul>
        )}
      </section>

      {canManage ? (
        <ManagedWorkspaceCreateDialog
          open={dialog.kind === 'create'}
          onOpenChange={(o) => {
            if (!o) setDialog({ kind: 'closed' })
          }}
          busy={createMutation.isPending}
          error={createMutation.isError}
          onSubmit={({ name, description, workspaceTemplateUuid }) =>
            createMutation.mutate({
              name,
              description,
              workspace_template_uuid: workspaceTemplateUuid,
            })
          }
        />
      ) : null}

      <WorkspaceExplainerDialog
        open={showExplainer}
        onOpenChange={setShowExplainer}
      />

      <WorkspaceFormDialog
        open={dialog.kind === 'edit'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        title={t('workspaces.edit')}
        submitLabel={t('common.save')}
        busy={editMutation.isPending}
        error={editMutation.isError}
        initialName={
          dialog.kind === 'edit' ? (dialog.workspace.name ?? '') : ''
        }
        initialDescription={
          dialog.kind === 'edit' ? (dialog.workspace.description ?? '') : ''
        }
        onSubmit={({ name, description }) => {
          if (dialog.kind !== 'edit') return
          editMutation.mutate({
            uuid: dialog.workspace.uuid,
            name,
            description,
          })
        }}
      />

      <DeleteConfirm
        open={dialog.kind === 'delete'}
        onOpenChange={(o) => {
          if (!o) setDialog({ kind: 'closed' })
        }}
        title={t('workspaces.deleteTitle')}
        description={t('workspaces.deleteDescription', {
          name:
            dialog.kind === 'delete' ? workspaceName(t, dialog.workspace) : '',
        })}
        busy={deleteMutation.isPending}
        onConfirm={() => {
          if (dialog.kind !== 'delete') return
          deleteMutation.mutate(dialog.workspace.uuid)
        }}
      />
    </PageContainer>
  )
}

function WorkspaceCardMenu({
  name,
  onEdit,
  onDelete,
}: {
  name: string
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          aria-label={t('workspaces.actionsFor', { name })}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          {t('common.edit')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onDelete} variant="destructive">
          {t('common.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Fetches workspace templates and renders the create dialog. Mounted only for
// managers — /workspace-templates is manager-scoped (403 without
// workspaces:manage), so a plain member never fetches it and the workspace list
// still loads.
function ManagedWorkspaceCreateDialog(
  props: Omit<ComponentProps<typeof WorkspaceCreateDialog>, 'templates'>,
) {
  const { data: templates } = useSuspenseQuery(workspaceTemplatesQueryOptions)
  return <WorkspaceCreateDialog templates={templates} {...props} />
}

type WorkspaceFormDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  submitLabel: string
  initialName?: string
  initialDescription?: string
  busy?: boolean
  error?: boolean
  onSubmit: (input: { name: string; description: string | null }) => void
}

function WorkspaceFormDialog(props: WorkspaceFormDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
        </DialogHeader>
        {props.open ? <WorkspaceFormBody {...props} /> : null}
      </DialogContent>
    </Dialog>
  )
}

function WorkspaceFormBody({
  onOpenChange,
  submitLabel,
  initialName = '',
  initialDescription = '',
  busy = false,
  error = false,
  onSubmit,
}: WorkspaceFormDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        const trimmedName = name.trim()
        if (!trimmedName || busy) return
        const trimmedDesc = description.trim()
        onSubmit({
          name: trimmedName,
          description: trimmedDesc.length > 0 ? trimmedDesc : null,
        })
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium" htmlFor="workspace-form-name">
          {t('workspaces.fields.name')}
        </label>
        <Input
          id="workspace-form-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('workspaces.fields.namePlaceholder')}
          disabled={busy}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label
          className="text-sm font-medium"
          htmlFor="workspace-form-description"
        >
          {t('workspaces.fields.description')}
        </label>
        <textarea
          id="workspace-form-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="min-h-20 rounded-md border bg-background px-3 py-2 text-sm shadow-xs"
          placeholder={t('workspaces.fields.descriptionPlaceholder')}
          disabled={busy}
        />
      </div>
      {error ? (
        <p className="text-sm text-destructive">{t('common.error')}</p>
      ) : null}
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={busy}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={busy || !name.trim()}>
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  )
}
